import { useState, useEffect, useCallback } from 'react';
import { 
  ProjectCreationSettingsService, 
  ProjectCreationSettings, 
  ProjectCreationEligibility 
} from '../services/projectCreationSettingsService';
import { useAuth } from '../context/AuthContext';
import { useErrorHandling } from './useErrorHandling';

/**
 * Hook for managing project creation settings with caching and real-time updates
 */
export const useProjectCreationSettings = () => {
  const [settings, setSettings] = useState<ProjectCreationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const { error, setError, clearError, executeWithErrorHandling } = useErrorHandling({
    onError: (error) => {
      console.error('Project creation settings error:', error);
    },
    autoRetry: true,
    maxRetries: 2
  });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    clearError();
    
    const result = await executeWithErrorHandling(
      () => ProjectCreationSettingsService.getSettings(),
      'load_project_creation_settings'
    );
    
    if (result) {
      setSettings(result);
    }
    setLoading(false);
  }, [executeWithErrorHandling, clearError]);

  useEffect(() => {
    loadSettings();

    // Subscribe to real-time updates
    const unsubscribe = ProjectCreationSettingsService.subscribeToUpdates(() => {
      // Create a stable callback that doesn't depend on loadSettings
      (async () => {
        try {
          const result = await ProjectCreationSettingsService.getSettings();
          if (result) {
            setSettings(result);
          }
        } catch (error) {
          console.error('Error reloading settings from subscription:', error);
        }
      })();
    });

    return unsubscribe;
  }, []); // Remove loadSettings from dependencies

  const updateSetting = useCallback(async (settingName: string, value: any) => {
    await executeWithErrorHandling(
      () => ProjectCreationSettingsService.updateSetting(settingName, value),
      'update_project_creation_setting'
    );
    
    // Immediately reload settings after successful update to ensure UI shows latest data
    await loadSettings();
  }, [executeWithErrorHandling, loadSettings]);

  return {
    settings,
    loading,
    error,
    updateSetting,
    refetch: loadSettings
  };
};

/**
 * Hook for checking project creation eligibility with caching and real-time updates
 */
export const useProjectCreationEligibility = () => {
  const { user } = useAuth();
  const [eligibility, setEligibility] = useState<ProjectCreationEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const { error, clearError, executeWithErrorHandling } = useErrorHandling({
    onError: (error) => {
      console.error('Project creation eligibility error:', error);
    },
    autoRetry: true,
    maxRetries: 1
  });

  const checkEligibility = useCallback(async () => {
    if (!user) {
      setEligibility(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    clearError();
    
    const result = await executeWithErrorHandling(
      () => ProjectCreationSettingsService.checkEligibility(user.id),
      'check_project_creation_eligibility'
    );
    
    if (result) {
      setEligibility(result);
    }
    setLoading(false);
  }, [user, executeWithErrorHandling, clearError]);

  useEffect(() => {
    checkEligibility();

    if (user) {
      // Subscribe to real-time updates that might affect eligibility
      const unsubscribe = ProjectCreationSettingsService.subscribeToUpdates(() => {
        // Create a stable callback that doesn't depend on checkEligibility
        (async () => {
          try {
            const result = await ProjectCreationSettingsService.checkEligibility(user.id);
            if (result) {
              setEligibility(result);
            }
          } catch (error) {
            console.error('Error checking eligibility from subscription:', error);
          }
        })();
      });

      return unsubscribe;
    }
  }, [user]); // Remove checkEligibility from dependencies

  return {
    eligibility,
    loading,
    error,
    refetch: checkEligibility,
    canCreate: eligibility?.canCreate ?? false
  };
};

/**
 * Hook for managing user points with caching
 */
export const useUserPoints = () => {
  const { user } = useAuth();
  const [points, setPoints] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const { error, clearError, executeWithErrorHandling } = useErrorHandling({
    onError: (error) => {
      console.error('User points error:', error);
    },
    autoRetry: true,
    maxRetries: 2
  });

  const loadPoints = useCallback(async () => {
    if (!user) {
      setPoints(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    clearError();
    
    const result = await executeWithErrorHandling(
      () => ProjectCreationSettingsService.getUserPoints(user.id),
      'load_user_points'
    );
    
    if (result !== null) {
      setPoints(result);
    }
    setLoading(false);
  }, [user, executeWithErrorHandling, clearError]);

  useEffect(() => {
    loadPoints();

    if (user) {
      // Subscribe to real-time updates for user points
      const unsubscribe = ProjectCreationSettingsService.subscribeToUpdates(() => {
        // Create a stable callback that doesn't depend on loadPoints
        (async () => {
          try {
            const result = await ProjectCreationSettingsService.getUserPoints(user.id);
            if (result !== null) {
              setPoints(result);
            }
          } catch (error) {
            console.error('Error reloading points from subscription:', error);
          }
        })();
      });

      return unsubscribe;
    }
  }, [user]); // Remove loadPoints from dependencies

  return {
    points,
    loading,
    error,
    refetch: loadPoints
  };
};

/**
 * Hook for managing cache operations
 */
export const useProjectCreationCache = () => {
  const { user } = useAuth();

  const invalidateUserCache = useCallback(() => {
    if (user) {
      ProjectCreationSettingsService.invalidateUserCache(user.id);
    }
  }, [user]);

  const invalidateAllCaches = useCallback(() => {
    ProjectCreationSettingsService.invalidateAllCaches();
  }, []);

  const getCacheStats = useCallback(() => {
    return ProjectCreationSettingsService.getCacheStats();
  }, []);

  return {
    invalidateUserCache,
    invalidateAllCaches,
    getCacheStats
  };
};